import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { FileChip } from "@/components/files/FileChip.tsx";
import { Button } from "@/components/ui/button.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { artifactQueries } from "@/hooks/artifactQueries.ts";
import { api } from "@/lib/http.ts";
import { uploadFile } from "@/lib/fileUpload.ts";
import { cn, resolveApiPath } from "@/lib/utils.ts";
import type { GetArtifactResp } from "@/types/artifacts.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Paperclip } from "lucide-react";
import { Fragment, useCallback, useRef, useState } from "react";

type EmailAttachmentsComponentData = Extract<ArtifactComponent, { type: "emailAttachments" }>;

/** A locally-tracked upload that hasn't landed on the artifact yet. */
interface PendingUpload {
  id: string;
  name: string;
  status: "uploading" | "error";
}

/**
 * The server's `accept` list for the file picker — same source the Omnibox uses,
 * so the draft input offers the exact set of types the attach endpoint accepts.
 * Cached indefinitely: the supported set is static per deploy.
 */
const useSupportedAccept = (): string | undefined => {
  const { data } = useQuery({
    queryKey: ["files", "supported-types"],
    queryFn: () =>
      api.get<{ htmlAccept: string }>("/api/a/files/supported-types").then((res) => res.data),
    staleTime: Number.POSITIVE_INFINITY,
  });
  return data?.htmlAccept;
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

/**
 * Posts/deletes against `.../artifacts/:id/attachments` and replaces the artifact
 * detail cache with the refreshed response — same cache-update contract as
 * `usePatchArtifact`, so the chips re-render from the server's source of truth.
 */
const useAttachmentMutation = (threadId: number, messageId: number, artifactId: string) => {
  const queryClient = useQueryClient();

  const add = useCallback(
    async (fileId: string) => {
      const res = await api.post<GetArtifactResp>(
        `/api/a/threads/${threadId}/messages/${messageId}/artifacts/${artifactId}/attachments`,
        { source: "gcsFile", fileId },
      );
      queryClient.setQueryData(
        artifactQueries.detail(threadId, messageId, artifactId).queryKey,
        res.data,
      );
    },
    [queryClient, threadId, messageId, artifactId],
  );

  const remove = useCallback(
    async (id: string) => {
      const res = await api.delete<GetArtifactResp>(
        `/api/a/threads/${threadId}/messages/${messageId}/artifacts/${artifactId}/attachments/${encodeURIComponent(id)}`,
      );
      queryClient.setQueryData(
        artifactQueries.detail(threadId, messageId, artifactId).queryKey,
        res.data,
      );
    },
    [queryClient, threadId, messageId, artifactId],
  );

  return { add, remove };
};

export const EmailAttachmentsBlock = ({
  component,
}: {
  component: EmailAttachmentsComponentData;
}) => {
  const { threadId, messageId, artifactId, isReadOnly, isSharedThread } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;
  const { add, remove } = useAttachmentMutation(threadId, messageId, artifactId);
  const acceptTypes = useSupportedAccept();

  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadAll = useCallback(
    async (files: File[]) => {
      const entries = files.map((file) => ({ file, localId: crypto.randomUUID() }));
      // One state update for the whole batch, so a multi-file drop paints every
      // chip at once instead of re-rendering per file.
      setPending((prev) => [
        ...prev,
        ...entries.map(({ file, localId }) => ({
          id: localId,
          name: file.name,
          status: "uploading" as const,
        })),
      ]);
      for (const { file, localId } of entries) {
        // Track which step failed: an attach failure after a successful upload
        // leaves an orphaned GCS object, so the two need distinct log lines.
        let stage = "upload";
        try {
          const { fileId } = await uploadFile(file);
          stage = `attach (fileId=${fileId})`;
          await add(fileId);
          setPending((prev) => prev.filter((p) => p.id !== localId));
        } catch (error) {
          console.error(`Couldn't attach "${file.name}" — ${stage} step failed:`, error);
          errorToast(`Couldn't attach "${file.name}"`);
          setPending((prev) => prev.map((p) => (p.id === localId ? { ...p, status: "error" } : p)));
        }
      }
    },
    [add],
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) void uploadAll(Array.from(e.target.files));
    e.target.value = ""; // allow re-selecting the same file
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!canEdit) return;
    if (e.dataTransfer.files?.length) void uploadAll(Array.from(e.dataTransfer.files));
  };

  const hasContent = component.attachments.length > 0 || pending.length > 0;
  // Nothing to show and nothing to do — render nothing rather than an empty box.
  if (!hasContent && !canEdit) return null;

  // Only an editable block is a drop target; read-only renders a plain container.
  const dropZoneProps = canEdit
    ? {
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          setIsDragging(true);
        },
        onDragLeave: () => setIsDragging(false),
        onDrop,
      }
    : {};

  return (
    <div
      {...dropZoneProps}
      className={cn(
        "flex flex-col gap-2 rounded-lg",
        isDragging && "outline outline-2 outline-dashed outline-primary/50 bg-primary/5",
      )}
    >
      {hasContent && (
        <div className="flex flex-wrap gap-2">
          {component.attachments.map((a) => {
            const chip = (
              <FileChip
                name={a.filename}
                subtitle={formatBytes(a.size)}
                onRemove={
                  canEdit
                    ? () => void remove(a.id).catch(() => errorToast("Couldn't remove attachment"))
                    : undefined
                }
                removeAriaLabel={`Remove ${a.filename}`}
                removeStatsigName="email-attachment-remove"
              />
            );
            // The download path rides the user's session, which a shared
            // thread's reader doesn't have, so there the chip stays inert.
            if (!a.href || isSharedThread) return <Fragment key={a.id}>{chip}</Fragment>;
            return (
              <a
                key={a.id}
                href={resolveApiPath(a.href)}
                className="rounded-lg hover:opacity-80"
                aria-label={`Download ${a.filename}`}
                data-statsig-name="email-attachment-download"
              >
                {chip}
              </a>
            );
          })}

          {pending.map((p) => (
            <FileChip
              key={p.id}
              name={p.name}
              subtitle={p.status === "error" ? "Upload failed" : "Uploading…"}
              status={p.status}
            />
          ))}
        </div>
      )}

      {canEdit && (
        <div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={acceptTypes}
            className="hidden"
            onChange={onInputChange}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => inputRef.current?.click()}
                aria-label="Attach a file"
                className="gap-2 text-muted-foreground"
                data-statsig-name="email-attachment-attach"
              >
                <Paperclip className="size-4" />
                Attach file
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Attach a file to this email</TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
};
